import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Drawer,
  EmptyState,
  Field,
  IconBtn,
  Input,
  PageHeader,
  Spinner,
  Table,
  useToast,
} from "../../../design-system";
import useCurrentMembership from "../../../helpers/useCurrentMembership";
import { formatInstant } from "../../../helpers/datetime";
import { listTimeTrackingAudits, verifyAuditChain } from "../../../helpers/kronaly_helper";

const ACTION_META = {
  create: { label: "Creación", tone: "success" },
  update: { label: "Modificación", tone: "info" },
  hard_delete: { label: "Eliminación definitiva", tone: "danger" },
  restore: { label: "Restauración", tone: "warning" },
};

const TYPE_LABEL = {
  time_entry: "Fichaje",
  workday_record: "Jornada",
  workday_break: "Pausa",
};

const REASON_LABEL = {
  clear_workday: "Vaciado de jornada",
  upsert_replace: "Reemplazo en edición",
  correction_apply: "Corrección aprobada",
  retention_purge: "Purga por retención (4 años)",
};

function unwrap(r) {
  if (r && typeof r === "object" && "data" in r) return r.data;
  return r;
}

function pad(n) { return String(n).padStart(2, "0"); }

function todayYmd() {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function daysAgoYmd(days) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// Instante de auditoría en la zona del workspace (con segundos).
function formatDateTime(iso, tz) {
  return formatInstant(iso, tz, {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  });
}

function shortId(id) {
  if (!id) return "—";
  return id.slice(0, 8);
}

export default function AuditoriaPage() {
  document.title = "Registro de auditoría | Kronaly";
  const { can, isManager, loading: membershipLoading, timezone } = useCurrentMembership();
  const toast = useToast();

  const canAccess = can("employees:manage") || can("time_tracking:rlt_view") || isManager;

  const [startDate, setStartDate] = useState(daysAgoYmd(30));
  const [endDate, setEndDate] = useState(todayYmd());
  const [actionFilter, setActionFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [audits, setAudits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [detail, setDetail] = useState(null);
  const [chain, setChain] = useState(null); // {checked, valid, first_mismatch_id} | null
  const [chainLoading, setChainLoading] = useState(false);

  const checkChain = useCallback(async () => {
    setChainLoading(true);
    try {
      const r = await verifyAuditChain();
      setChain(r?.data ?? r ?? null);
    } catch (_) {
      setChain(null);
    } finally {
      setChainLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!membershipLoading && canAccess) checkChain();
  }, [membershipLoading, canAccess, checkChain]);

  const load = useCallback(async () => {
    if (!startDate || !endDate) return;
    setLoading(true);
    setError(null);
    try {
      const r = await listTimeTrackingAudits({
        start_date: startDate,
        end_date: endDate,
      });
      setAudits(unwrap(r) || []);
    } catch (e) {
      const msg = typeof e === "string" ? e : e?.message || "No se pudieron cargar las auditorías.";
      setError(msg);
      toast.error("Error al cargar", msg);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, toast]);

  useEffect(() => {
    if (!membershipLoading && canAccess) load();
  }, [membershipLoading, canAccess, load]);

  const filtered = useMemo(() => {
    return audits.filter((a) => {
      if (actionFilter && a.action !== actionFilter) return false;
      if (typeFilter && a.auditable_type !== typeFilter) return false;
      return true;
    });
  }, [audits, actionFilter, typeFilter]);

  if (membershipLoading) {
    return (
      <div className="kr-page" style={{ display: "flex", justifyContent: "center", padding: 48 }}>
        <Spinner />
      </div>
    );
  }

  if (!canAccess) {
    return (
      <div className="kr-page">
        <EmptyState
          icon="ShieldOff"
          title="Acceso restringido"
          description="Necesitas permisos de gestión de empleados o la capability RLT para ver el registro de auditoría."
        />
      </div>
    );
  }

  return (
    <div className="kr-page">
      <PageHeader
        eyebrow="Cumplimiento · Fichaje"
        title="Registro de auditoría"
        description="Histórico inmutable de cambios en el registro horario. Cada fila incluye marca temporal, actor, IP, contenido previo/posterior y hash criptográfico encadenado (verificable con php artisan time-tracking:verify-audit-chain)."
      />

      <Alert tone="info">
        <strong>Inmutable y verificable.</strong> Este log es append-only — ninguna entrada puede
        modificarse ni eliminarse desde la aplicación. Cualquier alteración del histórico se
        detecta por la cadena de hashes SHA-256.
      </Alert>

      <ChainStatusBanner chain={chain} loading={chainLoading} onRecheck={checkChain} />


      {error && <Alert tone="danger">{error}</Alert>}

      <div style={filterRow}>
        <Field label="Desde">
          <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        </Field>
        <Field label="Hasta">
          <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
        </Field>
        <Field label="Acción">
          <select
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
            style={selectStyle}
          >
            <option value="">Todas</option>
            <option value="create">Creación</option>
            <option value="update">Modificación</option>
            <option value="hard_delete">Eliminación definitiva</option>
          </select>
        </Field>
        <Field label="Tipo">
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            style={selectStyle}
          >
            <option value="">Todos</option>
            <option value="time_entry">Fichaje</option>
            <option value="workday_record">Jornada</option>
            <option value="workday_break">Pausa</option>
          </select>
        </Field>
        <Button variant="primary" icon="RefreshCw" onClick={load} disabled={loading}>
          {loading ? "Cargando…" : "Actualizar"}
        </Button>
      </div>

      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 48 }}>
          <Spinner />
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon="FileSearch"
          title="Sin eventos en el rango"
          description={
            audits.length === 0
              ? "No hay registros de auditoría entre las fechas seleccionadas."
              : "Ningún evento coincide con los filtros aplicados. Prueba a relajar la acción o el tipo."
          }
        />
      ) : (
        <Table>
          <thead>
            <tr>
              <th style={th}>Fecha y hora</th>
              <th style={th}>Acción</th>
              <th style={th}>Recurso</th>
              <th style={th}>Actor</th>
              <th style={th}>IP</th>
              <th style={th}>Motivo</th>
              <th style={{ ...th, textAlign: "right" }}></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((a) => {
              const action = ACTION_META[a.action] || { label: a.action, tone: "neutral" };
              return (
                <tr key={a.id}>
                  <td style={{ ...td, fontFamily: "var(--kr-font-mono)", fontSize: 12 }}>
                    {formatDateTime(a.occurred_at, timezone)}
                  </td>
                  <td style={td}>
                    <Badge tone={action.tone}>{action.label}</Badge>
                  </td>
                  <td style={td}>
                    {TYPE_LABEL[a.auditable_type] || a.auditable_type}
                    <div style={subtleId} title={a.auditable_id}>{shortId(a.auditable_id)}…</div>
                  </td>
                  <td style={td}>
                    {a.actor_user_id ? (
                      <span title={a.actor_user_id} style={{ fontFamily: "var(--kr-font-mono)", fontSize: 12 }}>
                        {shortId(a.actor_user_id)}…
                      </span>
                    ) : (
                      <Badge tone="neutral">Sistema</Badge>
                    )}
                  </td>
                  <td style={{ ...td, fontFamily: "var(--kr-font-mono)", fontSize: 12 }}>
                    {a.ip || "—"}
                  </td>
                  <td style={td}>
                    {a.reason ? (REASON_LABEL[a.reason] || a.reason) : "—"}
                  </td>
                  <td style={{ ...td, textAlign: "right" }}>
                    <IconBtn icon="Eye" title="Ver detalle" onClick={() => setDetail(a)} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}

      <DetailDrawer audit={detail} onClose={() => setDetail(null)} tz={timezone} />
    </div>
  );
}

function ChainStatusBanner({ chain, loading, onRecheck }) {
  if (loading && !chain) {
    return (
      <Alert tone="neutral">
        <Spinner size="sm" /> Verificando integridad de la cadena…
      </Alert>
    );
  }
  if (!chain) return null;
  if (chain.checked === 0) {
    return (
      <Alert tone="neutral">
        Aún no hay eventos auditados en este workspace.
      </Alert>
    );
  }
  if (chain.valid) {
    return (
      <Alert tone="success">
        <strong>Cadena íntegra ✓</strong> · {chain.checked.toLocaleString("es-ES")} filas verificadas.
        Cada hash SHA-256 coincide con el cálculo esperado.{" "}
        <button onClick={onRecheck} style={inlineLink} disabled={loading}>
          {loading ? "Recomprobando…" : "Volver a comprobar"}
        </button>
      </Alert>
    );
  }
  return (
    <Alert tone="danger">
      <strong>Tampering detectado ✗</strong> · Discrepancia a partir de la fila{" "}
      <code>{chain.first_mismatch_id}</code>. {chain.checked.toLocaleString("es-ES")} filas
      recorridas. Ejecuta <code>php artisan time-tracking:verify-audit-chain</code> para inspección
      detallada y contacta con soporte.
    </Alert>
  );
}

function DetailDrawer({ audit, onClose, tz }) {
  const open = audit !== null;
  return (
    <Drawer open={open} onClose={onClose} title="Detalle del evento" size="lg">
      {audit && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <MetaRow label="ID" value={audit.id} mono />
            <MetaRow label="Fecha" value={formatDateTime(audit.occurred_at, tz)} />
            <MetaRow label="Acción" value={ACTION_META[audit.action]?.label || audit.action} />
            <MetaRow label="Tipo" value={TYPE_LABEL[audit.auditable_type] || audit.auditable_type} />
            <MetaRow label="Recurso" value={audit.auditable_id} mono />
            <MetaRow label="Actor" value={audit.actor_user_id || "Sistema"} mono />
            <MetaRow label="IP" value={audit.ip || "—"} mono />
            <MetaRow
              label="Motivo"
              value={audit.reason ? (REASON_LABEL[audit.reason] || audit.reason) : "—"}
            />
          </div>

          {audit.user_agent && (
            <div>
              <div style={metaLabel}>User-Agent</div>
              <div style={metaUaBox}>{audit.user_agent}</div>
            </div>
          )}

          <JsonBlock title="Estado anterior (before)" data={audit.before_json} emptyMsg="—" />
          <JsonBlock title="Estado posterior (after)" data={audit.after_json} emptyMsg="—" />
        </div>
      )}
    </Drawer>
  );
}

function MetaRow({ label, value, mono }) {
  return (
    <div>
      <div style={metaLabel}>{label}</div>
      <div style={{ ...metaValue, ...(mono ? { fontFamily: "var(--kr-font-mono)", fontSize: 12, wordBreak: "break-all" } : {}) }}>
        {value || "—"}
      </div>
    </div>
  );
}

function JsonBlock({ title, data, emptyMsg }) {
  const text = data ? JSON.stringify(data, null, 2) : emptyMsg;
  return (
    <div>
      <div style={metaLabel}>{title}</div>
      <pre style={jsonBox}>{text}</pre>
    </div>
  );
}

const th = {
  textAlign: "left",
  padding: "10px 16px",
  borderBottom: "1px solid var(--kr-border)",
  background: "var(--kr-bg-subtle)",
  fontSize: 11,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  color: "var(--kr-fg-muted)",
  fontWeight: 600,
};

const td = {
  padding: "12px 16px",
  borderBottom: "1px solid var(--kr-border-subtle)",
  verticalAlign: "middle",
};

const subtleId = {
  fontFamily: "var(--kr-font-mono)",
  fontSize: 11,
  color: "var(--kr-fg-muted)",
  marginTop: 2,
};

const filterRow = {
  display: "flex",
  gap: 12,
  alignItems: "flex-end",
  flexWrap: "wrap",
  marginBottom: 16,
};

const selectStyle = {
  padding: "8px 12px",
  borderRadius: 6,
  border: "1px solid var(--kr-border)",
  background: "var(--kr-bg)",
  color: "var(--kr-fg)",
  fontSize: 14,
  minWidth: 160,
};

const metaLabel = {
  fontSize: 11,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  color: "var(--kr-fg-muted)",
  fontWeight: 600,
  marginBottom: 4,
};

const metaValue = {
  fontSize: 14,
  color: "var(--kr-fg-strong)",
};

const metaUaBox = {
  background: "var(--kr-bg-subtle)",
  border: "1px solid var(--kr-border-subtle)",
  borderRadius: 4,
  padding: 8,
  fontFamily: "var(--kr-font-mono)",
  fontSize: 12,
  wordBreak: "break-all",
};

const inlineLink = {
  background: "none",
  border: "none",
  color: "var(--kr-success-700)",
  textDecoration: "underline",
  cursor: "pointer",
  padding: 0,
  font: "inherit",
};

const jsonBox = {
  background: "var(--kr-bg-subtle)",
  border: "1px solid var(--kr-border)",
  borderRadius: 6,
  padding: 12,
  fontFamily: "var(--kr-font-mono)",
  fontSize: 12,
  maxHeight: 320,
  overflow: "auto",
  margin: 0,
};
